// The Emails list for a record (customer, estimate, invoice, receipt, storage contract), built from the
// sent-email log (/emailLog, written by functions/mail.js). Opens over the page; updates live.
//
//   openEmailHistory({ customerId } | { recordType, recordId, customerId? }, {
//     title, sub, email, firstName,       the header ("Mark Ellison", "Sea Wren · Slip B-14 · mark@…")
//     compose: { ...openCompose options } the "Email {first name}" button (omit to hide it)
//     onOpenRecord(row)                   an "Open …" button on rows about another record
//     onFixAddress(newEmail)              failed sends to the customer: correct the address on file
//   })
//   countEmails({ customerId } | { recordId })  -> Promise<number>  for an "Emails 6" label

import { database } from './initFirebase';
import { ref, get, onValue, query, orderByChild, equalTo } from 'firebase/database';
import { toastMessage, escapeHTML } from './utilities';
import { openCompose, sendMail } from './mailClient';

const esc = v => escapeHTML(v == null ? '' : String(v));
const isEmail = s => /^[^\s@<>(),;:"]+@[^\s@<>(),;:"]+\.[^\s@<>(),;:"]{2,}$/.test(String(s || '').trim());
const initials = s => String(s || '?').split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0].toUpperCase()).join('') || '?';

const TYPE_LABEL = {
  invoice: 'Invoice', estimate: 'Estimate', contract: 'Contract', receipt: 'Receipt', statement: 'Statement',
  reminder: 'Reminder', battery: 'Battery', message: 'Message', test: 'Test', purchaseOrder: 'Purchase order',
};
const RECORD_LABEL = { estimate: 'estimate', invoice: 'invoice', payment: 'receipt', contract: 'contract', booking: 'booking', request: 'request', battery: 'battery report', purchaseOrder: 'purchase order' };
const FILTERS = ['All', 'Sent by staff', 'Automatic', 'Failed'];

/** Sent by a person (has their uid) or by the app on its own. */
export const isAutomatic = r => !!(r.autoKey || !r.sentByUid);
const kind = r => (r.status === 'failed' ? 'failed' : isAutomatic(r) ? 'auto' : 'staff');

function when(ms) {
  if (!ms) return '';
  const d = new Date(ms);
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const day = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  const t = d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }).replace(' AM', 'a').replace(' PM', 'p');
  return `${day} ${t}`;
}

function logQuery(scope) {
  if (scope.recordId) return query(ref(database, 'emailLog'), orderByChild('recordId'), equalTo(String(scope.recordId)));
  return query(ref(database, 'emailLog'), orderByChild('customerId'), equalTo(String(scope.customerId)));
}

export async function countEmails(scope) {
  if (!scope || (!scope.recordId && !scope.customerId)) return 0;
  try { return Object.keys((await get(logQuery(scope))).val() || {}).length; } catch { return 0; }
}

export function openEmailHistory(scope, opts = {}) {
  if (!scope || (!scope.recordId && !scope.customerId)) return;
  document.querySelector('.eh-backdrop')?.remove();
  // The look is in styles/mailCompose.css, which the compose window adds to the page.
  if (![...document.styleSheets].some(s => /mailCompose\.css/.test(s.href || '')) && !document.querySelector('link[data-mail-compose]')) {
    const l = document.createElement('link');
    l.rel = 'stylesheet'; l.href = '/styles/mailCompose.css'; l.dataset.mailCompose = '1';
    document.head.appendChild(l);
  }
  const S = { rows: [], filter: 'All', open: null, fixing: null, busy: null };
  const wrap = document.createElement('div');
  wrap.className = 'eh-backdrop';
  const first = opts.firstName || String(opts.title || '').split(' ')[0];
  wrap.innerHTML = `
    <div class="eh-card" role="dialog" aria-modal="true" aria-labelledby="ehTitle">
      <header class="eh-head">
        <span class="eh-av">${esc(initials(opts.title))}</span>
        <div class="eh-who"><div class="eh-name" id="ehTitle">${esc(opts.title || 'Emails')}</div>${opts.sub ? `<div class="eh-sub">${esc(opts.sub)}</div>` : ''}</div>
        ${opts.compose ? `<button type="button" class="mc-btn mc-primary" data-eh="compose">Email ${esc(first || 'them')}</button>` : ''}
        <button type="button" class="mc-x" data-eh="close" aria-label="Close">&times;</button>
      </header>
      <div class="eh-bar">
        <div class="eh-filters" role="tablist" aria-label="Show"></div>
        <span class="eh-note">From the sent-email log.</span>
      </div>
      <div class="eh-list" data-eh="list"><div class="eh-empty">Loading…</div></div>
    </div>`;
  document.body.appendChild(wrap);
  const list = wrap.querySelector('[data-eh="list"]');

  const shown = () => S.rows.filter(r => S.filter === 'All' || (S.filter === 'Sent by staff' ? kind(r) === 'staff' : S.filter === 'Automatic' ? kind(r) === 'auto' : kind(r) === 'failed'));
  const paintFilters = () => {
    const counts = { All: S.rows.length, 'Sent by staff': S.rows.filter(r => kind(r) === 'staff').length, Automatic: S.rows.filter(r => kind(r) === 'auto').length, Failed: S.rows.filter(r => kind(r) === 'failed').length };
    wrap.querySelector('.eh-filters').innerHTML = FILTERS.map(f => `<button type="button" role="tab" class="eh-pill${f === S.filter ? ' on' : ''}" aria-selected="${f === S.filter}" data-filter="${esc(f)}">${esc(f)}${f !== 'All' && counts[f] ? ` · ${counts[f]}` : ''}</button>`).join('');
  };
  const actionsFor = (r) => {
    const out = [];
    if (opts.onOpenRecord && r.recordType && r.recordId && !(scope.recordId && r.recordId === scope.recordId) && r.recordType !== 'customer') {
      out.push(['open', `Open ${RECORD_LABEL[r.recordType] || 'record'}`]);
    }
    out.push(['forward', 'Forward']);
    out.push(['resend', r.attachments?.length ? 'Resend (without attachments)' : 'Resend']);
    if (r.status === 'failed' && opts.onFixAddress) out.push(['fix', 'Fix address on file']);
    return out;
  };
  const paint = () => {
    paintFilters();
    const rows = shown();
    if (!S.rows.length) { list.innerHTML = '<div class="eh-empty">No emails yet. Emails sent from the app show here, with the automatic ones.</div>'; return; }
    if (!rows.length) { list.innerHTML = `<div class="eh-empty">Nothing under “${esc(S.filter)}”.</div>`; return; }
    list.innerHTML = rows.map(r => {
      const k = kind(r);
      const open = S.open === r.id;
      const subline = [r.attachments?.length ? r.attachments.join(', ') : '', `to ${r.to}`, r.resendOf ? 'resent' : ''].filter(Boolean).join(' · ');
      return `
        <div class="eh-row${open ? ' open' : ''}" data-key="${esc(r.id)}">
          <button type="button" class="eh-line" data-row="${esc(r.id)}" aria-expanded="${open}">
            <span class="eh-when">${esc(when(r.sentAt))}</span>
            <span class="eh-subj"><b>${esc(r.subject || '(no subject)')}</b><span>${esc(subline)}</span></span>
            <span class="eh-type">${esc(TYPE_LABEL[r.docType] || 'Message')}</span>
            <span class="eh-by">${esc(k === 'auto' || (k === 'failed' && isAutomatic(r)) ? 'Automatic' : r.sentBy || '')}</span>
            <span class="eh-status ${r.status === 'failed' ? 'bad' : 'ok'}"><i></i>${r.status === 'failed' ? 'Failed' : 'Sent'}</span>
          </button>
          ${open ? `<div class="eh-more">
            ${r.status === 'failed' && r.error ? `<div class="eh-error">${esc(r.error)}</div>` : ''}
            <div class="eh-preview">${r.bodyText ? esc(r.bodyText).replace(/\n/g, '<br>') : '<span class="mc-muted">The message text wasn’t kept for emails sent before the email update.</span>'}</div>
            ${S.fixing === r.id ? `<div class="eh-fix"><input type="email" data-eh="fixval" value="${esc(String(r.to || '').split(',')[0].trim())}" aria-label="Correct email address"><button type="button" class="mc-btn mc-primary mc-btn-sm" data-eh="fixsave">Save and resend</button><button type="button" class="mc-btn mc-btn-sm" data-eh="fixcancel">Cancel</button></div>` : ''}
            <div class="eh-actions">${actionsFor(r).map(([a, l]) => `<button type="button" class="mc-btn mc-btn-sm" data-act="${a}" data-id="${esc(r.id)}"${S.busy === r.id ? ' disabled' : ''}>${esc(l)}</button>`).join('')}</div>
          </div>` : ''}
        </div>`;
    }).join('');
  };

  const unsub = onValue(logQuery(scope), snap => {
    const rows = [];
    snap.forEach(c => { rows.push({ id: c.key, ...c.val() }); });
    S.rows = rows.sort((a, b) => (b.sentAt || 0) - (a.sentAt || 0));
    paint();
  }, e => { list.innerHTML = `<div class="eh-empty">Couldn’t read the email log (${esc(e.message)}).</div>`; });

  const resend = async (r, toOverride) => {
    const to = toOverride || r.to;
    if (!r.layout) {
      // Older rows have no layout to resend from: open it as a new email.
      close();
      openCompose({ to, cc: r.cc || '', subject: r.subject, body: r.bodyText || '', customerId: r.customerId || null, docType: r.docType, docLabel: r.docLabel, recordType: r.recordType, recordId: r.recordId });
      return;
    }
    let layout;
    try { layout = JSON.parse(r.layout); } catch { layout = null; }
    if (!layout) { toastMessage('This email can’t be resent as it was. Use Forward instead.', 'red'); return; }
    S.busy = r.id; paint();
    try {
      await sendMail({ to, cc: toOverride ? '' : (r.cc || ''), subject: r.subject, layout, customerId: r.customerId || null, docType: r.docType || 'message', docLabel: r.docLabel || null, recordType: r.recordType || null, recordId: r.recordId || null, resendOf: r.id });
      toastMessage(`Sent again to ${to}`, 'green');
    } catch (e) { toastMessage(e.message || 'It didn’t send', 'red', 6000); }
    S.busy = null; paint();
  };

  const close = () => { unsub(); document.removeEventListener('keydown', onKey, true); wrap.remove(); };
  const onKey = e => { if (e.key === 'Escape' && !document.querySelector('.mc-backdrop')) { e.stopPropagation(); close(); } };
  document.addEventListener('keydown', onKey, true);

  wrap.addEventListener('click', async e => {
    if (e.target === wrap || e.target.closest('[data-eh="close"]')) { close(); return; }
    if (e.target.closest('[data-eh="compose"]')) { openCompose({ ...(opts.compose || {}) }); return; }
    const f = e.target.closest('[data-filter]')?.dataset.filter;
    if (f) { S.filter = f; paint(); return; }
    const rowId = e.target.closest('[data-row]')?.dataset.row;
    if (rowId) { S.open = S.open === rowId ? null : rowId; S.fixing = null; paint(); return; }
    if (e.target.closest('[data-eh="fixcancel"]')) { S.fixing = null; paint(); return; }
    if (e.target.closest('[data-eh="fixsave"]')) {
      const r = S.rows.find(x => x.id === S.open);
      const v = wrap.querySelector('[data-eh="fixval"]').value.trim();
      if (!isEmail(v)) { toastMessage('That isn’t an email address.', 'red'); return; }
      try { await opts.onFixAddress(v); } catch (err) { toastMessage(err.message || 'Couldn’t save the address', 'red'); return; }
      S.fixing = null;
      await resend(r, v);
      return;
    }
    const act = e.target.closest('[data-act]');
    if (!act) return;
    const r = S.rows.find(x => x.id === act.dataset.id);
    if (!r) return;
    if (act.dataset.act === 'open') { close(); opts.onOpenRecord(r); }
    if (act.dataset.act === 'forward') {
      openCompose({
        to: '', subject: `Fwd: ${r.subject || ''}`,
        body: `\n\n---------- Forwarded email ----------\nSent ${when(r.sentAt)} to ${r.to}\nSubject: ${r.subject || ''}\n\n${r.bodyText || ''}`,
        signature: true, customerId: r.customerId || null, docType: 'message', recordType: r.recordType || null, recordId: r.recordId || null,
      });
    }
    if (act.dataset.act === 'resend') resend(r);
    if (act.dataset.act === 'fix') { S.fixing = r.id; paint(); wrap.querySelector('[data-eh="fixval"]')?.focus(); }
  });
}
